/**
 * NMS Product Gallery Enhancement Component
 *
 * Enhances the existing product gallery with additional lightbox functionality:
 * - Allows clicking on full .product-gallery__media elements (not just IMG tags)
 * - Works on all screen sizes (mobile and desktop)
 * - Integrates with existing PhotoSwipe lightbox system
 *
 * Built following NMS JavaScript standards - extends NMSBaseClass
 */

if (!window.NMSProductGalleryEnhance) {
  class NMSProductGalleryEnhance extends window.NMSBaseClass {
    constructor(element) {
      super();
      this.element = element; // product-gallery element

      // Store in global registry
      window.neemans = window.neemans || {};
      window.neemans.productGalleryEnhance = window.neemans.productGalleryEnhance || {};
      window.neemans.productGalleryEnhance[this.element.id || 'default'] = this;
    }

    init() {
      super.init();
      this.#setupRefs();
      this.#bindEvents();
    }

    // Setup element references
    #setupRefs() {
      this.refs.carousel = this.element.querySelector('.product-gallery__carousel');

      if (!this.refs.carousel) {
        console.error('NMS Product Gallery Enhance: carousel element not found');
        return;
      }
    }

    // Bind click events to media elements
    #bindEvents() {
      if (!this.refs.carousel) return;

      // Add click listeners to all product gallery media elements
      this.refs.carousel.addEventListener('click', this.#handleMediaClick.bind(this));
    }

    // Handle clicks on product gallery media
    #handleMediaClick(event) {
      // Only handle clicks on media containers or their children (but not the existing IMG click handler)
      const mediaElement = event.target.closest('.product-gallery__media');

      if (!mediaElement) {
        return; // Not clicking on a media element
      }

      // Check if this is an image media type
      if (mediaElement.getAttribute('data-media-type') !== 'image') {
        return; // Only handle image media
      }

      // Check if the gallery has zoom enabled
      if (!this.element.hasAttribute('allow-zoom')) {
        return; // Zoom not enabled
      }

      // Prevent default behavior
      event.preventDefault();

      // Get all image cells for index calculation
      const imageCells = Array.from(this.refs.carousel.querySelectorAll('.product-gallery__media')).filter(
        cell => cell.getAttribute('data-media-type') === 'image'
      );

      // Find the index of the clicked media
      const mediaIndex = imageCells.indexOf(mediaElement);

      if (mediaIndex === -1) {
        return; // Could not find media index
      }

      // Dispatch the lightbox:open event that the existing ProductGallery component listens for
      this.element.dispatchEvent(
        new CustomEvent('lightbox:open', {
          bubbles: true,
          detail: { index: mediaIndex },
        })
      );
    }

    // Clean up
    destroy() {
      super.destroy();

      // Clean up global reference
      if (window.neemans.productGalleryEnhance?.[this.element.id || 'default']) {
        delete window.neemans.productGalleryEnhance[this.element.id || 'default'];
      }
    }
  }

  // Make class available globally
  window.NMSProductGalleryEnhance = NMSProductGalleryEnhance;
}

// Initialize component when DOM is ready
function initNMSProductGalleryEnhance() {
  if (!window.NMSBaseClass) {
    console.error('NMSBaseClass required for NMSProductGalleryEnhance - make sure nms-base-class.js is loaded first');
    return;
  }

  // Find all product-gallery elements that have zoom enabled
  const elements = document.querySelectorAll('product-gallery[allow-zoom]');

  if (elements.length === 0) {
    return;
  }

  for (const element of elements) {
    if (!element.hasAttribute('data-nms-gallery-enhanced')) {
      try {
        const instance = new NMSProductGalleryEnhance(element);
        instance.init();
        element.setAttribute('data-nms-gallery-enhanced', 'true');
      } catch (error) {
        console.error('Failed to initialize product gallery enhancement:', error);
      }
    }
  }
}

// Auto-initialize
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initNMSProductGalleryEnhance);
} else {
  initNMSProductGalleryEnhance();
}

// Re-initialize after AJAX updates (for dynamic content)
document.addEventListener('product:rerender', initNMSProductGalleryEnhance);
